"use client"

import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Plus, Trash2, Edit2, GripVertical, X } from "@/lib/icons"
import { cn } from "@/lib/utils"
import type { SetGroup, TagSet } from "@/lib/tag-set-store"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslations } from "next-intl"

interface SetGroupManagerDialogProps {
  isOpen: boolean
  onClose: () => void
  groups: SetGroup[]
  sets: TagSet[]
  onCreateGroup: (name?: string, setIds?: string[]) => void
  onDeleteGroup: (groupId: string) => void
  onRenameGroup: (groupId: string, name: string) => void
  onReorderSets: (groupId: string, setIds: string[]) => void
  onMoveSetToGroup: (setId: string, fromGroupId: string | null, toGroupId: string | null) => void
}

interface SortableSetItemProps {
  set: TagSet
  onRemove: () => void
}

function SortableSetItem({ set, onRemove }: SortableSetItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: set.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-1 px-2 py-1 rounded border bg-secondary/75 border-border/50",
        isDragging && "shadow-lg",
      )}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-3 w-3 text-muted-foreground" />
      </div>
      <span className="text-xs font-medium flex-1">{set.name}</span>
      <Badge variant="outline" className="text-[10px] h-3 px-1">
        {set.tags.length}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-4 w-4 p-0 hover:bg-transparent"
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

export function SetGroupManagerDialog({
  isOpen,
  onClose,
  groups,
  sets,
  onCreateGroup,
  onDeleteGroup,
  onRenameGroup,
  onReorderSets,
  onMoveSetToGroup,
}: SetGroupManagerDialogProps) {
  const t = useTranslations("setGroupManagerDialog")
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ groupId: string; groupName: string } | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Get ungrouped sets
  const groupedSetIds = new Set(groups.flatMap((g) => g.setIds))
  const ungroupedSets = sets.filter((s) => !groupedSetIds.has(s.id))

  const handleRenameGroup = (groupId: string) => {
    if (editingName.trim()) {
      onRenameGroup(groupId, editingName.trim())
    }
    setEditingGroupId(null)
  }

  const handleDragEnd = (groupId: string) => (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const group = groups.find((g) => g.id === groupId)
      if (!group) return

      const oldIndex = group.setIds.indexOf(active.id as string)
      const newIndex = group.setIds.indexOf(over.id as string)

      const newSetIds = arrayMove(group.setIds, oldIndex, newIndex)
      onReorderSets(groupId, newSetIds)
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t("manageSetGroups")}</DialogTitle>
            <DialogDescription>
              {t("organizeYourTagSetsIntoGroupsForBetterOr")}
            </DialogDescription>
          </DialogHeader>

        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            {/* Existing Groups */}
            {groups.map((group) => {
              const groupSets = group.setIds
                .map((id) => sets.find((s) => s.id === id))
                .filter(Boolean) as TagSet[]

              return (
                <div key={group.id} className="border border-border/50 rounded-lg p-3 space-y-2">
                  {/* Group Header */}
                  <div className="flex items-center justify-between">
                    {editingGroupId === group.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => handleRenameGroup(group.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRenameGroup(group.id)
                            if (e.key === "Escape") setEditingGroupId(null)
                          }}
                          autoFocus
                          className="h-7 text-sm"
                        />
                      </div>
                    ) : (
                      <div 
                        className="flex items-center gap-2 cursor-pointer"
                        onDoubleClick={() => {
                          setEditingGroupId(group.id)
                          setEditingName(group.name)
                        }}
                        title={t("doubleClickToRename")}
                      >
                        <h3 className="font-semibold text-sm">{group.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {t("lengthSets", { length: groupSets.length })}
                        </Badge>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setEditingGroupId(group.id)
                          setEditingName(group.name)
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirmation({ groupId: group.id, groupName: group.name })}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Group Sets - Draggable */}
                  {groupSets.length > 0 ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd(group.id)}
                    >
                      <SortableContext items={groupSets.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-1">
                          {groupSets.map((set) => (
                            <SortableSetItem
                              key={set.id}
                              set={set}
                              onRemove={() => onMoveSetToGroup(set.id, group.id, null)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">{t("noSetsInThisGroup")}</p>
                  )}
                </div>
              )
            })}

            {/* Ungrouped Sets */}
            {ungroupedSets.length > 0 && (
              <div className="border border-dashed border-border/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">{t("ungroupedSets")}</h3>
                  <Badge variant="outline" className="text-xs">
                    {t("lengthSets", { length: ungroupedSets.length })}
                  </Badge>
                </div>

                <div className="space-y-1">
                  {ungroupedSets.map((set) => (
                    <div
                      key={set.id}
                      className="flex items-center gap-1 px-2 py-1 rounded border bg-secondary/50 border-border/30"
                    >
                      <span className="text-xs font-medium flex-1">{set.name}</span>
                      <Badge variant="outline" className="text-[10px] h-3 px-1">
                        {set.tags.length}
                      </Badge>
                      {groups.length > 0 && (
                        <select
                          className="text-xs border rounded px-1 py-0.5 bg-background"
                          onChange={(e) => {
                            if (e.target.value) {
                              onMoveSetToGroup(set.id, null, e.target.value)
                            }
                          }}
                          value=""
                        >
                          <option value="">{t("addTo")}</option>
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Group */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCreateGroup()}
              className="w-full"
            >
              <Plus className="h-3 w-3 mr-2" />
              {t("newGroup")}
            </Button>
          </div>
        </ScrollArea>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onClose}>{t("done")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmation} onOpenChange={(open) => !open && setDeleteConfirmation(null)}>
        <AlertDialogContent className="max-w-md sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteGroupGroupName", { groupName: deleteConfirmation?.groupName ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("setsWillNotBeDeleted")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmation(null)}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteConfirmation) {
                  onDeleteGroup(deleteConfirmation.groupId)
                  setDeleteConfirmation(null)
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
